/** The canvas side panels and modals: Layers, Pages, Memory, Activity, the
 *  prompt bar, Share, Export, frame history, imports and the prototype player. */
export const panels = {
  layersTitle: 'Layers',
  layersCollapsePanel: 'Collapse panel',
  layersExpandPanel: 'Expand panel',
  layersCollapseAll: 'Collapse all',
  layersNewFrame: 'New frame',
  layersEmpty: 'No frames yet. Press + to add one, or ask the agent for a design.',
  layersNoMatch: 'Nothing matches “{query}”.',
  layersSearch: 'Search layers',
  layersRailAria: 'Layers panel',
  layersRailCount: { one: 'Layers · {count} frame', other: 'Layers · {count} frames' },
  layersDesigning: 'designing…',
  layersEmptyFrame: 'empty frame',
  layersShow: 'Show',
  layersHide: 'Hide',
  layersLock: 'Lock',
  layersUnlock: 'Unlock',

  pagesTitle: 'Pages',
  pagesAdd: 'Add page',
  pagesName: 'Page name',
  pagesRename: 'Rename',
  pagesDuplicate: 'Duplicate page',
  pagesDelete: 'Delete page',
  pagesMoveSelection: 'Move selection to page',
  pagesMoveSelectionPlaceholder: 'Move selection to page…',
  pagesMoveFailed: 'Could not move all selected frames to the page',
  pagesSaveFailed: 'Could not save pages',
  pagesDuplicateFailed: 'Could not duplicate the page',

  memoryIntro: 'Memory is how this canvas remembers your taste — and how every agent designs with it.',
  memoryReferences: 'References',
  memoryRules: 'Rules',
  memoryDecisions: 'Decisions',
  memoryIntroReferences:
    '— pin a frame you love (Pin as reference in its inspector). Agents copy its colors, type and layout when they design something new.',
  memoryIntroRules: '— style guides agents read before designing. Write them, or let them grow.',
  memoryIntroDecisions:
    '— feedback you give agents is captured here automatically once it’s addressed. When a preference keeps recurring, Doop suggests adding it to your rules.',
  memorySuggestion: 'Memory suggestion',
  memoryFromDecisions: { one: 'from {count} decision', other: 'from {count} decisions' },
  memoryDismiss: 'Dismiss',
  memoryAddToRules: 'Add to rules',
  memoryNoReferences:
    'No references yet. Pin a frame you like (Pin as reference in its inspector) and agents will copy its colors, type and layout in new designs.',
  memoryNew: '+ New',
  memoryRulesHint: 'Style guides every agent reads before designing here.',
  memoryDistilled: 'distilled',
  memoryReference: 'Reference',
  memoryReferenceGone: 'This reference is no longer pinned.',
  memoryPinnedBy: 'pinned by {name} · {date} — agents copy this design’s colors, type and layout in new work',
  memoryUnpin: 'Unpin',
  memorySaveFailed: 'save failed',
  memoryNewRule: 'New rule',
  memoryEditRule: 'Edit rule',
  memoryRule: 'Rule',
  memoryNamePlaceholder: 'Name, e.g. “Featured Images”',
  memoryId: 'id:',
  memoryRulePlaceholder: '# Rules\n\nPalette, fonts, layout recipes, asset URLs…',
  memorySaving: 'Saving…',
  memoryGuideGone: 'This design guide no longer exists.',
  memoryEditedBy: 'edited by {name} · {date}',
  memoryDeleteTitle: 'Delete “{title}”?',
  memoryDeleteDescription:
    'Agents stop designing with this rule from their next task. Its version history is kept, so you can restore it later.',
  memoryDeleteConfirm: 'Delete guide',
  memoryHistory: 'History',
  memoryEdit: 'Edit',
  memoryHistoryFailed: 'could not load history',
  memoryHistoryTitle: '{title} — history',
  memorySavedBy: 'saved by {name} · {date}',
  memoryDeletedBy: 'deleted by {name} · {date}',
  memoryDeletionVersion: 'This version marks a deletion — there is nothing to show.',
  memoryRestoring: 'Restoring…',
  memoryRestore: 'Restore this version',
  memoryNoVersions: 'No versions recorded yet.',
  memoryVersionDeleted: 'deleted',
  memoryVersionCurrent: 'current',

  activityPanelAria: 'Agents panel',
  activityAgents: 'Agents',
  activityComments: 'Comments',
  activityCommentsOpen: { one: 'Comments · {count} open', other: 'Comments · {count} open' },
  activityCommentsTitle: 'Comments — every thread pinned on this page',
  activityActivity: 'Activity',
  activityMemory: 'Memory',
  activityMemoryTitle: 'Design memory — references, rules and decisions every agent on this canvas designs with',
  activityClosePanel: 'Close panel',
  activityRecentTasks: 'Recent tasks',
  activityNoTasks: 'No tasks yet. Ask the Doop Agent below, or connect your own — what agents work on shows up here.',
  activityWorkingOn: 'Working on',
  activityWorkedOn: 'Worked on',
  activityShowLess: 'Show less',
  activityShowAll: 'Show all',
  activityTaskUnfinished: 'The agent did not finish this task.',
  activityGoTo: 'Go to “{name}”',
  activityFor: 'for {name}',
  activityStopTitle: 'Stop this task',
  activityStop: 'Stop',
  activityRetry: 'Retry',
  activityEmpty: 'No activity yet. Add a frame, or connect an agent.',

  promptSendFailed: 'Something went wrong — try again',
  promptElement: 'element',
  promptTooBig: '“{name}” exceeds the 5 MB limit',
  promptTooMany: { one: 'Up to {count} image per request', other: 'Up to {count} images per request' },
  promptRemoveAttachment: 'Remove {name}',
  promptScopedFrame: 'Scoped to the frame “{name}”',
  promptScopedElement: 'Scoped to {name} in this frame',
  promptRemoveFrameScope: 'Remove frame scope',
  promptRemoveElementScope: 'Remove element scope',
  promptAria: 'Ask the Doop Agent',
  promptPlaceholderFrame: 'What should change in this frame…',
  promptPlaceholderElement: 'What should change in this element…',
  promptPlaceholder: 'Ask Doop to design something…',
  promptAttach: 'Attach images',
  promptAttachTitle: 'Attach screenshots or images',
  promptSendTooltip: 'Send (Enter)',
  promptSend: 'Send',
  promptSent: 'The Doop Agent is on it — watch the canvas',

  modelFallback: 'Model',
  modelAria: 'Model: {label}',
  modelCaption: 'Doop Agent model',
  modelNoAccount: 'No model account connected — the Doop Agent runs on the server’s model when one is set up.',
  modelSettings: 'Model settings…',
  modelConnect: 'Connect a model…',

  shareAccessNone: 'No access',
  shareAccessView: 'View',
  shareAccessComment: 'Comment',
  shareAccessEdit: 'Edit',
  shareSummaryNone: 'Invite-only canvas',
  shareSummaryView: 'Anyone with the link can view',
  shareSummaryComment: 'Anyone with the link can comment',
  shareSummaryEdit: 'Anyone with the link can edit',
  shareCategoryWebsite: 'Websites',
  shareCategoryApp: 'Web apps',
  shareCategoryDashboard: 'Dashboards',
  shareCategoryMobile: 'Mobile',
  shareCategoryMarketing: 'Marketing',
  shareCategoryOther: 'Other',
  shareMoveFailed: 'move failed',
  shareInviteFailed: 'invite failed',
  shareRemoveFailed: 'removal failed',
  shareAccessFailed: 'access update failed',
  sharePublishFailed: 'publish failed',
  shareCopyFailed: 'Couldn’t copy the link. Copy the URL from your browser instead.',
  shareTitle: 'Share “{name}”',
  shareInvitePlaceholder: 'Invite by email (doop account)',
  shareInvite: 'Invite',
  shareYou: '{name} (you)',
  shareOwner: 'Owner',
  shareLeave: 'Leave this canvas',
  shareRemove: 'Remove {name}',
  shareCanEdit: 'Can edit',
  shareEveryoneIn: 'Everyone in {name}',
  shareEveryoneInWorkspace: 'Everyone in the workspace',
  sharePeople: { one: '{count} person', other: '{count} people' },
  shareWorkspaceMembers: 'workspace members',
  shareWorkspace: 'Workspace',
  shareWorkspaceAria: 'Workspace this canvas lives in',
  sharePersonal: 'Personal — invite-only',
  shareNeedsPlan: '(needs a plan)',
  shareAnyoneCan: 'Anyone with the link can…',
  shareLinkAria: 'What anyone with the link can do',
  shareLinkTitle: 'No access = only you, invited people and workspace members can open this canvas',
  shareCopyLink: 'Copy link',
  shareUpgradeReason: 'Moving a canvas into a workspace needs a Team plan.',
  shareGalleryTitle: 'Anyone on doop can preview and copy a listed canvas. The canvas itself stays private.',
  shareGallery: 'Show in the community gallery',
  shareGalleryBlurb: 'People can preview it and copy it into their own account. Your canvas stays private.',
  shareDescriptionPlaceholder: 'What is this design? One or two lines helps people find it.',
  shareShelf: 'Gallery shelf',
  shareUpdateListing: 'Update listing',

  exportNothing: 'Nothing to export yet',
  exportNothingLede: 'Add a frame to this page first.',
  exportPdfTitle: 'Export as PDF',
  exportPdfLede:
    'The frames you check become one PDF, a vector page per frame at its own size, in the order below: top to bottom, then left to right on the canvas.',
  exportSelectAll: 'Select all',
  exportClear: 'Clear',
  exportFramesAria: 'Frames to export',
  exportUntitled: 'Untitled frame',
  exportPdfMax: { one: 'A PDF holds at most {count} frame.', other: 'A PDF holds at most {count} frames.' },
  exportUncheck: { one: 'Uncheck {count} frame to download.', other: 'Uncheck {count} frames to download.' },
  exportPdfHint: 'Text stays selectable. A long document can take a few seconds per page.',
  exportDownloadPdf: 'Download PDF',
  exportDownloadPdfPages: { one: 'Download PDF ({count} page)', other: 'Download PDF ({count} pages)' },
  exportFailed: 'Couldn’t export the {label}',
  exportTimedOut: 'The {label} export timed out — try again',
  exportSaveFailed: 'Couldn’t save “{name}”',
  exportReady: '“{name}” is ready',
  exportingMore: { one: 'Exporting {count} more…', other: 'Exporting {count} more…' },
  exportingFormat: 'Exporting {label}…',
  exportingPdf: 'Exporting PDF…',
  exportingPdfFrames: { one: 'Exporting PDF of {count} frame…', other: 'Exporting PDF of {count} frames…' },
  copyPngUnsupported: 'This browser can’t copy images',
  copyingPng: 'Copying PNG…',
  copyPngFailed: 'Couldn’t copy the PNG',
  copyPngTimedOut: 'The PNG copy timed out — try again',
  copyPngDone: 'PNG copied',

  frameExportTitle: 'Export “{name}”',
  frameExportLede: 'Download this frame as a PNG or JPG image, or as a vector PDF.',
  frameExportPng: 'Download PNG',
  frameExportJpg: 'Download JPG',
  frameExportPdfGroup: { one: 'Download PDF of {count} frame', other: 'Download PDF of {count} frames' },
  frameExportPdfGroupHint: 'One vector page per frame, top to bottom then left to right',
  frameExportPdfHint: 'Vector page the size of the frame, text stays selectable',

  historyTitle: '“{name}” — version history',
  historyLoadFailed: 'Couldn’t load the version history',
  historyVersionFailed: 'Couldn’t load this version',
  historyRestoreFailed: 'Couldn’t restore this version',
  historyRestored: 'Restored “{name}” to the version from {when}',
  historyCurrentBy: 'current design · last edit by',
  historyBy: 'by',
  historyLoadingVersion: 'Loading this version…',
  historyLede:
    'Every design a redesign, stream or restore replaced is kept here. Pick one to preview it, then restore it for everyone on the canvas.',
  historyCurrent: 'Current',
  historyEmpty: 'No earlier versions yet — they appear once a design is replaced.',

  importTitle: 'Import into Doop',
  importLede: 'Pick a source. Imports that find several pages or frames show a review before anything lands.',
  importSoon: 'Soon',
  importWebsite: 'Website',
  importWebsiteBlurb: 'One page, or discover a whole site.',
  importLiveApp: 'Live app',
  importLiveAppBlurb: 'Behind a login, a VPN or localhost.',
  importGithub: 'GitHub repo',
  importGithubBlurb: 'Screens read from the repo’s routes.',

  protoScreenGone: 'That screen is no longer on the canvas',
  protoOverlayGone: 'That overlay is no longer on the canvas',
  protoAria: 'Prototype: {name}',
  protoMissing: 'Missing screen',
  protoMissingLower: 'missing screen',
  protoRestart: 'Restart prototype',
  protoHotspots: 'Show hotspots',
  protoNewTab: 'Open prototype in a new tab',
  protoScaling: 'Scaling',
  protoScalingRecommended: 'Recommended',
  protoScalingOther: 'Other scaling options',
  protoScaleActual: 'Actual size (100%)',
  protoScaleResponsive: 'Responsive',
  protoScaleFitWidth: 'Fit width',
  protoScaleFit: 'Fit width and height',
  protoScaleFill: 'Fill screen',
  protoExit: 'Exit prototype',
  protoExitTitle: 'Exit (Esc)',
  protoDeleted: 'This screen was deleted from the canvas.',
  protoMissingFrame: 'missing frame',
  protoStart: 'Start',
  protoCloseOverlay: 'Close overlay',
  protoScriptElement: 'A script creates this element — wire it in the HTML',
  protoLinked: 'Click now goes to “{name}”',
  protoHandle: 'Drag onto a frame to link it',
  protoNotFound: 'Prototype not found',
  protoNotFoundText: 'This canvas doesn’t exist, or the link is wrong.',
  protoBackToCanvases: 'Back to your canvases',
  protoNothing: 'Nothing to play yet',
  protoNothingText: 'This page of the canvas has no frames.',
  protoOpenCanvas: 'Open the canvas',
} as const
